import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { FormsErrors } from '../../../../hooks/Forms/FormsErrors';
import GenericPromises from '../../../../api/GenericPromises';
import useSnackBar from '../../../../components/Commons/SnackBar/useSnackBar';
import { OptionMenu } from '../../../../interfaces/Security/optionsMenu';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Autocomplete, Box, Switch, TextField, Typography } from '@mui/material';
import { Spinner } from '../../../../components/Commons/Spinner/Spinner';
import { Resource } from '../../../../interfaces/Security/resources';
import { ButtonStyled, PrimaryButton } from '../../../../theme/buttons';
import InfoIcon from '@mui/icons-material/Info';

export const AddOptionMenu = ({ ...props }) => {
    const [t] = useTranslation("global");
    const { GenericPostResource, GenericGetResource, } = GenericPromises();
    const { GetError } = FormsErrors();
    const [isMenu, setIsMenu] = useState(false);
    const [menuParentsCombo, setMenuParentsCombo] = useState<OptionMenu[]>([]);
    const [comboResources, setComboResources] = useState<Resource[]>([])
    const [loadingPost, setLoadingPost] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);
    const { showSnackBar, SnackbarComponent } = useSnackBar();

    const {
        handleSubmit,
        control,
        formState: { errors },
        getValues,
        setValue,
        reset,
        trigger,
    } = useForm<OptionMenu>({
        defaultValues: {
            parent_option_id: undefined,
            resource_id: undefined,
            option_name: undefined,
            description: undefined,
            menu_order: undefined,
            is_visible: true,
        }
    });
    const onSubmit: SubmitHandler<OptionMenu> = (data) => onPost(data);

    const onPost = (data: OptionMenu) => {
        setLoadingPost(true);
        trigger().then(async (triggerData) => {
            if (triggerData) {
                let myOptionMenu = {
                    parent_option_id: data.parent_option_id,
                    resource_id: data.resource_id ?? null,
                    option_name: data.option_name,
                    description: data.description ?? null,
                    is_visible: data.is_visible,
                    menu_order: data.menu_order,
                }
                GenericPostResource("/optionmenu", myOptionMenu)
                    .then((response) => {
                        let myNewRow = {
                            ...response.data,
                        }
                        props.AddNewRow(myNewRow);
                        props.setOpenDialog(false);
                    })
                    .catch((error) => {
                        setLoadingPost(false);
                        showSnackBar(error.message, 'error');
                    });
            }
        })
        setLoadingPost(false);
    }

    const onCancel = () => {
        props.setOpenDialog(false);
    }

    const getNodesWithoutResource = (tree: any): any => {
        let result: OptionMenu[] = [];
        const traverse = (node: any): void => {
            if (node.resource_id === undefined || node.resource_id === null) {
                result.push(node);
            }
            if (node.children && node.children.length > 0) {
                node.children.forEach(traverse);
            }
        };

        traverse(tree);

        return result;
    };

    useEffect(() => {
        let myPromises = [
            GenericGetResource("/resources"),
        ];
        Promise.all(myPromises)
            .then((responses) => {
                reset({
                    parent_option_id: props.defaultOptionMenu.option_menu_id,
                    is_visible: true
                });
                let nodesWithoutResource = getNodesWithoutResource(props.defaultOptionMenuData);
                setMenuParentsCombo(nodesWithoutResource);
                setComboResources((prev) => responses[0].data.data);
                setDataLoaded(true);
            })
            .catch((error) => {
                showSnackBar(error.message, "error");
            })
    }, []);

    return (
        <>
            {!dataLoaded && <Spinner isBox={false} />}
            {dataLoaded &&
                <Box className='d-flex justify-content-center' sx={{ minWidth: 1000 }}>

                    <form onSubmit={handleSubmit(onSubmit)} className='w-100'>


                        <div className='w-100'>
                            <div className='w-100 d-flex'>
                                <div className='w-50'>
                                    <Controller
                                        name="parent_option_id"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field }) => (
                                            <Autocomplete
                                                ref={field.ref}
                                                size="small"
                                                sx={{ width: "100%" }}
                                                options={menuParentsCombo}
                                                defaultValue={props.defaultOptionMenu}
                                                getOptionLabel={(option) => `${option.description}`}
                                                renderOption={(props, option: OptionMenu) => (
                                                    <div key={option.option_menu_id}>
                                                        <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                                                            {`${option.description}`}
                                                        </Box>
                                                    </div>
                                                )}
                                                isOptionEqualToValue={(option, value) => option.option_menu_id === value.option_menu_id}
                                                onChange={(_, values) => {
                                                    field.onChange(values?.option_menu_id || null)
                                                }}
                                                disabled={true}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label={`${t("optionsmenu.fields.parent_option_id")}`}
                                                        variant="filled"
                                                        value={field.value}
                                                        error={!!errors.option_menu_id || field.value === null}
                                                        helperText={(errors.option_menu_id?.type || field.value === null) ? t("generic.forms-errors.required") : null}
                                                        sx={{ paddingRight: 2 }}
                                                    />
                                                )}
                                            />

                                        )}
                                    />
                                </div>
                                <div className='w-50'>
                                    <Controller
                                        name="resource_id"
                                        control={control}
                                        rules={{}}
                                        render={({ field }) => (
                                            <Autocomplete
                                                ref={field.ref}
                                                size="small"
                                                sx={{ width: "100%" }}
                                                options={comboResources}
                                                getOptionLabel={(option) => `${option.resource_name} - ${option.resource_route}`}
                                                renderOption={(props, option: Resource) => (
                                                    <div key={option.resource_id}>
                                                        <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                                                            {`${option.resource_name} - ${option.resource_route}`}
                                                        </Box>
                                                    </div>
                                                )}
                                                isOptionEqualToValue={(option, value) => option.resource_id === value.resource_id}
                                                onChange={(_, values) => {
                                                    if (values) {
                                                        setIsMenu(true);
                                                        setValue("option_name", values.resource_name ?? "")
                                                    }
                                                    else {
                                                        setValue("option_name", "")
                                                        setIsMenu(false);
                                                    }
                                                    field.onChange(values?.resource_id || null)
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label={`${t("resources.title-view")}`}
                                                        variant="filled"
                                                        value={field.value}
                                                        error={!!errors.resource_id}
                                                        helperText={GetError(errors.resource_id?.type)}
                                                    />
                                                )}
                                            />
                                        )}
                                    />
                                </div>
                            </div>

                            <div className='w-100 d-flex mt-3'>
                                <div className="w-50">
                                    <Controller
                                        name="option_name"
                                        control={control}
                                        rules={{ required: true, maxLength: 200 }}
                                        render={({ field }) =>
                                            <TextField
                                                variant="filled"
                                                label={`${t("optionsmenu.fields.option_name")}`}
                                                ref={field.ref}
                                                value={field.value}
                                                onChange={(event) => { field.onChange(event.target.value.replace(/\s+/g, '')) }}
                                                error={!!errors.option_name}
                                                helperText={GetError(errors.option_name?.type)}
                                                size="small"
                                                disabled={isMenu}
                                                InputLabelProps={{ shrink: true }}
                                                style={{ width: "100%" }}
                                                sx={{ paddingRight: 2 }}
                                            />
                                        }
                                    />
                                </div>
                                <div className="w-50">
                                    <Controller
                                        name="description"
                                        control={control}
                                        rules={{ maxLength: 200 }}
                                        render={({ field }) =>
                                            <TextField
                                                variant="filled"
                                                label={`${t("optionsmenu.fields.description")}`}
                                                ref={field.ref}
                                                value={field.value}
                                                onChange={(event) => { field.onChange(event.target.value) }}
                                                error={!!errors.description}
                                                helperText={GetError(errors.description?.type)}
                                                size="small"
                                                style={{ width: "100%" }}
                                            />
                                        }
                                    />
                                </div>
                            </div>

                            <div className='d-flex w-100 mt-3'>
                                <div className='w-50'>
                                    <Controller
                                        name="menu_order"
                                        control={control}
                                        rules={{ required: true }}
                                        defaultValue={1000}
                                        render={({ field }) =>
                                            <TextField
                                                variant="filled"
                                                label={`${t("optionsmenu.fields.menu_order")}`}
                                                type='number'
                                                ref={field.ref}
                                                sx={{
                                                    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none", },
                                                    "& input[type=number]": { MozAppearance: "textfield", }, paddingRight: 2
                                                }}
                                                value={field.value}
                                                onChange={(event) => { field.onChange(event.target.value) }}
                                                error={!!errors.menu_order}
                                                helperText={GetError(errors.menu_order?.type)}
                                                size="small"
                                                style={{ width: "100%" }}
                                            />
                                        }
                                    />
                                </div>
                                <div className='w-25'>
                                    <Controller
                                        name="is_visible"
                                        control={control}
                                        render={({ field }) =>
                                            <Box sx={{ display: 'flex', alignItems: 'center', width: '75%', justifyContent: "space-between" }}>
                                                <Typography sx={{ ml: 2 }}>{t("optionsmenu.fields.is_visible")}</Typography>
                                                <Switch ref={field.ref} onChange={field.onChange} value={field.value} defaultChecked />
                                            </Box>
                                        }
                                    />
                                </div>
                                <div className="d-flex flex-row w-25 mt-2">
                                    <div>
                                        <InfoIcon />
                                    </div>
                                    <div>
                                        {isMenu ?
                                            <Typography sx={{ ml: 2 }}>{t("optionsmenu.info.is_resource")}</Typography>
                                            :
                                            <Typography sx={{ ml: 2 }}>{t("optionsmenu.info.is_menu")}</Typography>
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='d-flex flex-row-reverse mt-1'>
                            <PrimaryButton onClick={() => { onPost(getValues()) }} variant='outlined' className='m-1' disabled={loadingPost}>{t("generic.buttons.accept")}</PrimaryButton>
                            <ButtonStyled variant='contained' className='m-1' onClick={onCancel} disabled={loadingPost}>{t("generic.buttons.cancel")}</ButtonStyled>
                        </div>
                    </form>
                </Box >
            }
            <SnackbarComponent />
        </>
    )
}
